import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import TableContainer from '@mui/material/TableContainer';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { Table } from '@components/Table';
import { CopyIcon } from 'assets/images/icons';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation, useHasAccessToAsset } from '@hooks';
import { RIGHTS } from '@constants/user';
import { formatAssignedEntities } from '@helpers/formatEntitiesToString';
import { FORM_TYPE } from '../EditModal';

const CroppedText = styled(Typography)({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 400,
    '@media (max-width: 1024px)': {
        maxWidth: 200,
    },
});

const GetTableHeader = () => {
    const { t } = useTranslation();
    return [
        {
            key: 0,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.title')}
                </Typography>
            ),
        },
        {
            key: 1,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.type')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.locations')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: <Typography align="left" variant="subtitle2"></Typography>,
        },
    ];
};

const GetTableBody = (data, onClickAction, locations, brands) => {
    const { t } = useTranslation();
    const { hasAccessToAssetByIds } = useHasAccessToAsset();
    const ChatbotContentTypeMap = {
        1: 'Q&A',
    };
    return data.map(content => {
        const rights = hasAccessToAssetByIds(content);
        return [
            {
                component: (
                    <Typography variant="subtitle2" align="left">
                        {content.title}
                    </Typography>
                ),
            },
            {
                component: (
                    <CroppedText variant="body2" align="left">
                        {ChatbotContentTypeMap[content.type]}
                    </CroppedText>
                ),
            },
            {
                component: (
                    <Typography style={{ width: 220 }} variant="body2" align="left">
                        {content.locations.length > 0
                            ? formatAssignedEntities(locations, content.locations, {
                                  idKey: 'companyId',
                                  labelKey: 'internalName',
                                  limit: 30,
                              })
                            : formatAssignedEntities(brands, content.brands, {
                                  idKey: 'id',
                                  labelKey: 'title',
                                  limit: 30,
                              })}
                    </Typography>
                ),
            },
            ...(rights[RIGHTS.WRITE]
                ? [
                      {
                          component: (
                              <Typography align="right" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <Tooltip title={t('common.edit')} placement="top">
                                      <IconButton onClick={() => onClickAction(content, FORM_TYPE.EDIT)}>
                                          <EditIcon viewBox="0 0 22 22" />
                                      </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('common.duplicate')} placement="top">
                                      <IconButton
                                          sx={{ width: '36px' }}
                                          onClick={() => onClickAction(content, FORM_TYPE.EDIT_COPY)}
                                      >
                                          <CopyIcon style={{ fill: '#000', width: 18 }} />
                                      </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('common.delete')} placement="top">
                                      <IconButton onClick={() => onClickAction(content, FORM_TYPE.DELETE)}>
                                          <DeleteIcon sx={{ path: { fill: 'rgba(0,0,0,.6)' } }} />
                                      </IconButton>
                                  </Tooltip>
                              </Typography>
                          ),
                      },
                  ]
                : [
                      {
                          component: (
                              <Typography align="right">
                                  <Tooltip title={t('common.view')} placement="top">
                                      <IconButton onClick={() => onClickAction(content, FORM_TYPE.VIEW)}>
                                          <VisibilityIcon viewBox="0 0 22 22" />
                                      </IconButton>
                                  </Tooltip>
                              </Typography>
                          ),
                      },
                  ]),
        ];
    });
};

const KnowledgeBaseTable = ({
    setModal,
    setSelectedContent,
    onPageChange,
    locations,
    brands,
    page,
    onRowsPerPageChange,
    rowsPerPage,
    setDeleteModalOpen,
}) => {
    const { t } = useTranslation();
    const { data, loading, totalCount, pages } = useSelector(state => state.knowledgeBase);

    const onClickAction = (content, type) => {
        setSelectedContent({
            ...content,
            ...(type === FORM_TYPE.EDIT_COPY && { name: `${content.name} (${t('common.copy')})` }),
        });
        if (type === FORM_TYPE.DELETE) {
            setDeleteModalOpen(true);
            return;
        }
        setModal({ open: true, type });
    };

    return (
        <TableContainer>
            <Table
                withHover
                columnNames={GetTableHeader()}
                rows={GetTableBody(data, onClickAction, locations, brands)}
                isDataLoading={loading}
                withPagination
                paginationProps={{
                    colSpan: 5,
                    count: totalCount,
                    rowsPerPage,
                    page,
                    onPageChange,
                    onRowsPerPageChange,
                    labelDisplayedRows: ({ from, to, count }) =>
                        `${from}–${to} ${t('Table.of')} ${count !== -1 ? count : `${t('Table.moreThan')} ${to}`}`,
                    labelRowsPerPage: <span>{t('Table.rowsPerPage')}</span>,
                    pages,
                }}
            />
        </TableContainer>
    );
};

export default KnowledgeBaseTable;
